import { useEffect } from 'react';
import { useNavigate, useBlocker, useBeforeUnload, useLocation } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { Button, Tooltip, IconButton, Spinner } from '@crazyegginc/hatch';

import { DashHeader } from '/src/components/headers/DashHeader';
import { Breadcrumb, BreadcrumbItem } from '/src/components/Breadcrumb';
import { SitePickerBar } from '/src/components/site-picker';
import { getFunnelsQueryParams } from '/src/utils/url';
import { useFunnelEditorContext } from '../../funnel-editor-context';

import { usePermissions, useModal, useSelectedSite, useAuthContext } from '/src/hooks';

import { FEATURES } from '/src/features/_global/constants';
import { getFeatureName } from '/src/features/_global/utils';
import { RenameFunnelModal } from '../modals/RenameFunnelModal';
import { SaveFunnelModal } from '../modals/SaveFunnelModal';
import { PopoverActions } from '../actions';

import { ReactComponent as SaveIcon } from '@crazyegginc/hatch/dist/images/icon-save.svg';
import { ReactComponent as EditIcon } from '@crazyegginc/hatch/dist/images/icon-pencil-filled.svg';
import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';
import { AppLink } from '/src/features/_global/components/AppLink';

export function ReportHeader() {
  const { funnel } = useFunnelEditorContext();
  const modal = useModal();
  const navigate = useNavigate();
  const { selectedSite } = useSelectedSite();
  const permissions = usePermissions();
  const canEditFunnel = permissions.can('edit', FEATURES.FUNNELS).allowed;
  const { values, setFieldValue } = useFormikContext();
  const { isSharing } = useAuthContext();

  return (
    <>
      <DashHeader
        titleComponent={
          <Breadcrumb>
            {!isSharing ? (
              <BreadcrumbItem
                active={false}
                to={{
                  pathname: `/funnels`,
                  search: getFunnelsQueryParams() ?? '',
                }}
              >
                Funnels
              </BreadcrumbItem>
            ) : null}
            <BreadcrumbItem active={true}>
              <div className="flex space-x-2 items-center" data-testid="funnel-name-breadcrumb">
                <span>{values.name}</span>
                {funnel && canEditFunnel ? (
                  <Tooltip placement="bottom" tooltipContent="Rename Funnel">
                    <IconButton
                      icon={<EditIcon className="h-4 w-4 fill-current" />}
                      className="h-full shrink-0 text-malibu-500 hover:text-dodger-blue-500"
                      onClick={() => {
                        modal.show(
                          <RenameFunnelModal
                            funnel={{ ...funnel, name: values.name }}
                            onSuccess={(newFunnel) => {
                              setFieldValue('name', newFunnel.name);
                            }}
                          />,
                        );
                      }}
                      label="edit funnel name"
                    />
                  </Tooltip>
                ) : null}
              </div>
            </BreadcrumbItem>
          </Breadcrumb>
        }
        actionButton={
          <div className="flex shrink-0 items-center space-x-2.5">
            {!isSharing ? (
              <Button
                component={AppLink}
                variant="ghost-primary"
                to={{
                  pathname: `/funnels`,
                  search: getFunnelsQueryParams(),
                }}
                size="xl"
                className="text-center leading-none"
              >
                <ArrowIcon className="mr-2 h-3 w-3 shrink-0 rotate-180 fill-current" />
                Back to all Funnels
              </Button>
            ) : null}
            {funnel && canEditFunnel ? (
              <PopoverActions
                funnel={{ ...funnel, name: values.name }}
                onDelete={() => navigate({ pathname: '/funnels', search: getFunnelsQueryParams() })}
                onRename={(newFunnel) => setFieldValue('name', newFunnel.name)}
              />
            ) : null}
            {canEditFunnel ? <Save /> : null}
          </div>
        }
      />
      <SitePickerBar
        selectedSite={selectedSite}
        loading={false}
        sites={[selectedSite]}
        addSiteOption={false}
        pageDisplayName={`${getFeatureName(FEATURES.FUNNELS)} report`}
      />
    </>
  );
}

function Save() {
  const modal = useModal();
  const location = useLocation();
  const { isSubmitting, isValid, dirty, submitCount, values, setFieldValue, submitForm } = useFormikContext();
  const disabled = !dirty || !isValid || isSubmitting;

  const wasSaved = location.state?.submitCount > 0 || submitCount > 0;

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      dirty &&
      currentLocation.pathname !== nextLocation.pathname &&
      currentLocation.pathname.startsWith('/funnels/edit') &&
      !(currentLocation.pathname === '/funnels/edit' && nextLocation.pathname.startsWith('/funnels/edit/'))
    );
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      modal.show(
        <SaveFunnelModal
          name={values.name}
          onSave={async (name) => {
            await setFieldValue('name', name);
            await setFieldValue('inSaveModal', true);
            setTimeout(async () => {
              await submitForm();
              modal.close();
              blocker.proceed();
            }, 10);
          }}
          onCancel={() => {
            blocker.proceed();
            modal.close();
          }}
          onStay={() => {
            blocker.reset();
            modal.close();
          }}
        />,
      );
    }
  }, [blocker, modal, setFieldValue, submitForm, values.name]);

  useBeforeUnload((event) => {
    if (!dirty) return;
    event.preventDefault();
    event.returnValue = true;
  });

  return (
    <Button leftIcon={isSubmitting ? null : SaveIcon} type="submit" disabled={disabled}>
      {isSubmitting ? (
        <>
          <Spinner />
          <span className="ml-2.5">Saving...</span>
        </>
      ) : disabled && wasSaved ? (
        'Saved'
      ) : (
        'Save'
      )}
    </Button>
  );
}
